import { Sparkles, ArrowUpRight } from "lucide-react";
import { formatINR, priceOf } from "@/config";
import { comboMembers } from "@/lib/combo";

export const ComboStrip = ({ offers, products, onPreview }) => {
  const combos = offers.filter((o) => o.deal_type === "bundle" && o.product_ids?.length);
  if (!combos.length) return null;

  return (
    <div className="mb-10" data-testid="combo-strip">
      <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-[#F5C45A]">
        <Sparkles size={12} /> Combo deals
      </div>
      <p className="mt-1 text-[11px] text-slate-500">One combo or total-price offer applies per order — the biggest saving is used.</p>
      <div className="mt-4 grid gap-4 md:grid-cols-2">
        {combos.map((combo) => {
          const members = comboMembers(combo, products);
          const complete = members.every(({ product }) => product);
          const full = members.reduce((sum, { product, qty }) => sum + priceOf(product) * qty, 0);
          const saving = complete ? Math.max(0, full - combo.bundle_price) : 0;
          const image = combo.image || members.find(({ product }) => product?.is_live !== false && product?.image)?.product.image;
          return (
            <div
              key={combo.id}
              data-testid={`combo-card-${combo.id}`}
              className="overflow-hidden rounded-2xl border border-[#F5C45A]/40 bg-gradient-to-br from-[#2A0A0E] to-[#12141A]"
            >
              <button
                type="button" data-testid={`combo-view-${combo.id}`} onClick={() => onPreview(combo)}
                className="group block w-full text-left transition-colors hover:bg-white/[0.03] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#F5C45A]"
              >
              <span className="flex gap-4 p-4">
                {image && (
                  <img src={image} alt={combo.title} data-testid={`combo-image-${combo.id}`} onError={(event) => { event.currentTarget.onerror = null; event.currentTarget.src = "/placeholder-product.svg"; }} className="h-24 w-24 shrink-0 rounded-xl bg-white object-contain" />
                )}
                <span className="min-w-0 flex-1 [overflow-wrap:anywhere]">
                  <span data-testid={`combo-title-${combo.id}`} className="block font-display text-lg font-bold text-[#FFE9B0]">{combo.title}</span>
                  <span className="mt-0.5 block text-xs text-slate-400" data-testid={`combo-items-${combo.id}`}>
                    {members.map(({ product, qty }) => `${product?.name || "Unavailable product"}${qty > 1 ? ` ×${qty}` : ""}`).join(" + ")}
                  </span>
                  <span className="mt-2 flex flex-wrap items-baseline gap-2">
                    <span className="font-display text-xl font-extrabold text-[#F5C45A]" data-testid={`combo-price-${combo.id}`}>
                      {formatINR(complete ? Math.min(full, combo.bundle_price) : combo.bundle_price)}
                    </span>
                    {saving > 0 && (
                      <>
                        <span data-testid={`combo-subtotal-${combo.id}`} className="font-mono text-xs text-slate-400 line-through">{formatINR(full)}</span>
                        <span data-testid={`combo-saving-${combo.id}`} className="rounded-full border border-[#F5C45A]/40 px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.15em] text-[#F5C45A]">
                          Save {formatINR(saving)}
                        </span>
                      </>
                    )}
                  </span>
                </span>
              </span>
              <span className="flex min-h-12 w-full items-center justify-center gap-2 border-t border-[#F5C45A]/30 bg-[#F5C45A]/10 py-3 text-xs font-bold uppercase tracking-[0.15em] text-[#FFE9B0] transition-colors group-hover:bg-[#F5C45A]/20">
                View combo <ArrowUpRight size={16} aria-hidden="true" />
              </span>
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};
